import { createRandom } from '../core/random.js';
import { type ShapeDefinition, ShapeSink, type Vec3 } from './shape.js';

/** The sphere a baked form occupies, used to frame the camera on it. */
export interface Bounds {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly radius: number;
}

const EMPTY_BOUNDS: Bounds = { x: 0, y: 0, z: 0, radius: 1 };
const BOUNDS_BUCKETS = 96;
/** Frame the sphere holding this share of the particles, ignoring the stragglers. */
const BOUNDS_COVERAGE = 0.87;

/**
 * Turns a shape definition into the two float buffers the GPU reads: `form`
 * (xyz + palette tint) and `motion` (idle drift vector + phase).
 *
 * The work is time-sliced. A quarter of a million particles takes tens of
 * milliseconds to place, which would drop a frame; `step()` places as many as
 * it is given budget for and returns false until the whole form is ready, so a
 * bake spreads invisibly across the seconds while the previous form is on screen.
 */
export class ShapeBake {
  public readonly form: Float32Array;
  public readonly motion: Float32Array;
  private readonly particles: number;
  private readonly sink: ShapeSink;
  private place: ((index: number, sink: ShapeSink) => void) | null = null;
  private placed = 0;
  private currentBounds: Bounds = EMPTY_BOUNDS;

  constructor(particles: number) {
    this.particles = particles;
    this.form = new Float32Array(particles * 4);
    this.motion = new Float32Array(particles * 4);
    this.sink = new ShapeSink(this.form, this.motion);
  }

  public get done(): boolean {
    return this.place === null;
  }

  public get bounds(): Bounds {
    return this.currentBounds;
  }

  /** Start baking a new form. Discards any bake in progress. */
  public begin(shape: ShapeDefinition, seed: number, variant: number): void {
    const random = createRandom(seed);
    this.sink.rewind();
    this.placed = 0;
    this.place = shape.prepare({ random, total: this.particles, variant }).place;
  }

  /** Place up to `budget` more particles. Returns true when the form is complete. */
  public step(budget: number): boolean {
    const place = this.place;
    if (place === null) {
      return true;
    }
    const until = Math.min(this.particles, this.placed + budget);
    for (let i = this.placed; i < until; i += 1) {
      place(i, this.sink);
      // The sink is committed here, not by the shape: a shape that bails out of
      // placing a particle simply repeats the last one instead of leaving a hole.
      this.sink.emit();
    }
    this.placed = until;
    if (this.placed < this.particles) {
      return false;
    }
    this.currentBounds = this.measure();
    this.place = null;
    return true;
  }

  /**
   * The sphere the form's *body* occupies. A plain bounding box would be thrown
   * out by outliers — a galaxy's halo or a room's dust make the box several
   * times the size of the thing you are meant to be looking at, and the camera
   * then frames a speck. So this takes the centroid and the radius containing
   * most of the particles, which is what the eye reads as the subject.
   */
  private measure(): Bounds {
    if (this.particles === 0) {
      return EMPTY_BOUNDS;
    }
    const centre = this.centroid();
    const furthest = this.spread(centre, null);
    if (furthest <= 0) {
      return { ...centre, radius: 1 };
    }
    return { ...centre, radius: Math.max(1, this.spread(centre, furthest)) };
  }

  private centroid(): Vec3 {
    let sumX = 0;
    let sumY = 0;
    let sumZ = 0;
    for (let i = 0; i < this.particles; i += 1) {
      const at = i * 4;
      sumX += this.form[at] ?? 0;
      sumY += this.form[at + 1] ?? 0;
      sumZ += this.form[at + 2] ?? 0;
    }
    return {
      x: sumX / this.particles,
      y: sumY / this.particles,
      z: sumZ / this.particles,
    };
  }

  private distanceFrom(centre: Vec3, index: number): number {
    const at = index * 4;
    return Math.hypot(
      (this.form[at] ?? 0) - centre.x,
      (this.form[at + 1] ?? 0) - centre.y,
      (this.form[at + 2] ?? 0) - centre.z,
    );
  }

  /**
   * With `furthest` null, returns the distance to the outermost particle. With a
   * value, returns the radius that covers `BOUNDS_COVERAGE` of them, found by
   * histogramming distances into that range.
   */
  private spread(centre: Vec3, furthest: number | null): number {
    if (furthest === null) {
      let widest = 0;
      for (let i = 0; i < this.particles; i += 1) {
        widest = Math.max(widest, this.distanceFrom(centre, i));
      }
      return widest;
    }
    const buckets = new Uint32Array(BOUNDS_BUCKETS);
    for (let i = 0; i < this.particles; i += 1) {
      const ratio = this.distanceFrom(centre, i) / furthest;
      const slot = Math.min(BOUNDS_BUCKETS - 1, Math.floor(ratio * BOUNDS_BUCKETS));
      buckets[slot] = (buckets[slot] ?? 0) + 1;
    }
    const wanted = this.particles * BOUNDS_COVERAGE;
    let seen = 0;
    for (let slot = 0; slot < BOUNDS_BUCKETS; slot += 1) {
      seen += buckets[slot] ?? 0;
      if (seen >= wanted) {
        return ((slot + 1) / BOUNDS_BUCKETS) * furthest;
      }
    }
    return furthest;
  }
}
